// JSON export of a single analysis.
//
// The payload is a flat, self-describing document: whoever opens it later
// should not need this application to understand it, so every rate carries the
// period it was divided by and every absent value is left out rather than
// written as a zero. Nothing is sent anywhere; the file is built in the page
// and handed to the browser's own download.

export const EXPORT_SCHEMA = 'rio-dashboard.combat-log-report'
export const EXPORT_VERSION = 1

function round(value, digits = 2) {
    const number = Number(value)
    if (!Number.isFinite(number)) return 0
    const factor = 10 ** digits
    return Math.round(number * factor) / factor
}

function perSecond(total, ms) {
    if (!ms) return null
    return round((total * 1000) / ms)
}

function exportPlayer(player, basisMs) {
    return {
        name: player.name,
        guid: player.guid,
        realm: player.realm || null,
        class: player.className || null,
        spec: player.spec || null,
        role: player.role || null,
        itemLevel: player.itemLevel || null,
        activeMs: player.activeMs,
        damage: player.damage,
        dps: perSecond(player.damage, basisMs),
        dpsActive: perSecond(player.damage, player.activeMs),
        healing: player.healing,
        hps: perSecond(player.healing, basisMs),
        overhealing: player.overhealing,
        damageTaken: player.damageTaken,
        damageTakenAbsorbed: player.damageTakenAbsorbed,
        interrupts: player.interrupts,
        dispels: player.dispels,
        deaths: player.deaths,
        casts: player.casts,
    }
}

function exportDeath(death) {
    return {
        name: death.name,
        guid: death.guid,
        at: new Date(death.ts).toISOString(),
        offsetMs: death.offsetMs,
        encounter: death.encounterName || null,
        windowMs: death.windowMs,
        killingBlow: death.killingBlow
            ? {
                spellId: death.killingBlow.spellId || null,
                spell: death.killingBlow.spellName || null,
                source: death.killingBlow.sourceName || null,
                amount: death.killingBlow.amount,
                overkill: death.killingBlow.overkill,
            }
            : null,
        activeAuras: (death.activeAuras || []).map((aura) => ({
            spellId: aura.spellId,
            name: aura.name,
            type: aura.auraType,
            stacks: aura.stacks,
            appliedAt: aura.appliedAt ? new Date(aura.appliedAt).toISOString() : null,
        })),
        events: (death.events || []).map((event) => ({
            at: new Date(event.ts).toISOString(),
            kind: event.kind,
            spellId: event.spellId || null,
            spell: event.spellName || null,
            source: event.sourceName || null,
            amount: event.amount || 0,
            absorbed: event.absorbed || 0,
            overkill: event.overkill || 0,
            healthAfter: event.hpAfter === undefined ? null : event.hpAfter,
            maxHealth: event.maxHp === undefined ? null : event.maxHp,
        })),
    }
}

export function buildReportExport({ report, summary, file, basis = 'segment' }) {
    const basisMs = basis === 'active' ? null : (report.basisMs || report.durationMs || 0)

    return {
        schema: EXPORT_SCHEMA,
        version: EXPORT_VERSION,
        generatedAt: new Date().toISOString(),
        source: {
            fileName: summary ? summary.fileName : (file ? file.name : null),
            fileSize: file ? file.size : null,
            logVersion: summary ? summary.logVersion : null,
            buildVersion: summary ? summary.buildVersion : null,
            advancedLogging: summary ? summary.advancedLogging : null,
            lineCount: summary ? summary.lineCount : null,
            eventCount: summary ? summary.eventCount : null,
            unknownEvents: summary ? summary.unknownEvents : {},
            owner: summary && summary.logOwner ? summary.logOwner.name : null,
        },
        segment: {
            key: report.key,
            type: report.type,
            name: report.name,
            start: report.startTs ? new Date(report.startTs).toISOString() : null,
            end: report.endTs ? new Date(report.endTs).toISOString() : null,
            durationMs: report.durationMs,
            fightMs: report.fightMs || null,
            activeMs: report.activeMs,
            // Which period the rates below were divided by, spelled out so the
            // numbers cannot be read against the wrong clock.
            rateBasis: basis === 'active' ? 'each actor\'s own active combat time' : 'the duration of this segment',
            rateBasisMs: basisMs,
            meta: report.meta || null,
        },
        totals: report.totals,
        players: (report.players || []).map((player) => exportPlayer(player, basisMs || player.activeMs)),
        others: (report.others || []).map((player) => exportPlayer(player, basisMs || player.activeMs)),
        abilities: report.abilities || { damage: [], healing: [] },
        enemies: report.enemies || [],
        damageTakenSources: report.damageTakenSources || [],
        deaths: (report.deaths || []).map(exportDeath),
        timeline: {
            bucketMs: report.timeline ? report.timeline.bucketMs : null,
            length: report.timeline ? report.timeline.length : 0,
            startTs: report.timeline && report.startTs ? new Date(report.startTs).toISOString() : null,
            group: report.timeline ? report.timeline.total : null,
        },
    }
}

export function exportFileName(report, summary) {
    const base = (summary && summary.fileName ? summary.fileName : 'combat-log')
        .replace(/\.txt$/i, '')
    const segment = report.type === 'log'
        ? 'whole-log'
        : report.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    return `${base}-${segment || report.key}.json`
}

// The browser does the saving; the page only hands it a blob and a name. The
// object URL is released on the next frame, once the click has been consumed.
export function downloadJson(fileName, payload) {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    requestAnimationFrame(() => URL.revokeObjectURL(url))
    return blob.size
}
